import { useState, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/features/auth/AuthProvider'
import type { CajaSesion, CajaMovimiento } from '@/types/database'
import {
  useCajaActual, useCajaMovimientos, useAbrirCaja, useCerrarCaja, useAgregarMovimientoCaja,
  useCajaSesiones, type MetodoCaja,
} from './hooks'
import { CloseIcon } from '@/components/shared/Icons'
import NuevaVentaPage from '@/features/ventas/NuevaVentaPage'

const fmt = (n: number) =>
  new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

const fmtHora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })

function duracion(fechaApertura: string): string {
  const mins = Math.floor((Date.now() - new Date(fechaApertura).getTime()) / 60000)
  if (mins < 60) return `${mins} min`
  const hrs = Math.floor(mins / 60)
  const rem = mins % 60
  return rem > 0 ? `${hrs} h ${rem} min` : `${hrs} h`
}

function computeKpis(sesion: CajaSesion, movimientos: CajaMovimiento[]) {
  const net = { efectivo: 0, transferencia: 0, debito: 0, credito: 0 }
  for (const m of movimientos) {
    const sign = m.tipo === 'ingreso' ? 1 : -1
    net[m.metodo as keyof typeof net] += sign * m.monto
  }
  return {
    efectivoEnCaja: sesion.monto_apertura + net.efectivo,
    transferencias: net.transferencia,
    debito: net.debito,
    credito: net.credito,
  }
}

export default function CajaPage() {
  const { user } = useAuth()
  const { data: sesion, isLoading } = useCajaActual(user?.id)

  if (isLoading) {
    return <div style={{ color: 'var(--me-text-3)', padding: 8 }}>Cargando estado de caja...</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--me-space-8)' }}>
      {!sesion
        ? <CajaCerradaView usuarioId={user?.id ?? ''} />
        : <CajaAbiertaView sesion={sesion} usuarioId={user?.id ?? ''} />
      }
      <HistorialSesiones />
    </div>
  )
}

// ─── Caja cerrada ─────────────────────────────────────────────────────────────

function CajaCerradaView({ usuarioId }: { usuarioId: string }) {
  const [nombreEmpleado, setNombreEmpleado] = useState('')
  const [monto, setMonto] = useState('')
  const abrir = useAbrirCaja()

  async function handleAbrir() {
    if (!nombreEmpleado.trim()) {
      toast.error('Ingresá el nombre del empleado que inicia el turno')
      return
    }
    try {
      await abrir.mutateAsync({ montoApertura: parseFloat(monto) || 0, usuarioId, nombreEmpleado })
      toast.success('Turno abierto')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al abrir caja')
    }
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: 'calc(100vh - 160px)',
    }}>
      <div className="me-card" style={{ width: 360, padding: 32 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: 'var(--me-acid)',
          clipPath: 'polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%)',
          marginBottom: 20,
        }} />
        <div className="me-eyebrow" style={{ marginBottom: 4 }}>Caja</div>
        <h1 className="me-page-title" style={{ marginBottom: 24 }}>Abrir turno</h1>

        <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Nombre del empleado</label>
        <input
          className="me-input"
          style={{ width: '100%', marginBottom: 16 }}
          type="text"
          value={nombreEmpleado}
          onChange={e => setNombreEmpleado(e.target.value)}
          placeholder="Ej: Juan, María..."
          autoFocus
          autoComplete="off"
        />

        <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>Efectivo inicial</label>
        <div style={{ position: 'relative', marginBottom: 24 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--me-text-3)', fontSize: 14, pointerEvents: 'none',
          }}>$</span>
          <input
            className="me-input"
            style={{ width: '100%', paddingLeft: 24, fontFamily: 'var(--me-mono)', fontSize: 16 }}
            type="number"
            min="0"
            step="0.01"
            value={monto}
            onChange={e => setMonto(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAbrir()}
            placeholder="0,00"
          />
        </div>
        <button
          className="me-btn me-btn-primary"
          style={{ width: '100%' }}
          onClick={handleAbrir}
          disabled={abrir.isPending}
        >
          {abrir.isPending ? 'Abriendo...' : 'Abrir turno'}
        </button>
      </div>
    </div>
  )
}

// ─── Caja abierta ─────────────────────────────────────────────────────────────

function CajaAbiertaView({ sesion, usuarioId }: { sesion: CajaSesion; usuarioId: string }) {
  const { data: movimientos = [] } = useCajaMovimientos(sesion.id)
  const [showCierre, setShowCierre] = useState(false)
  const [tab, setTab] = useState<'vender' | 'movimientos'>('vender')
  const kpis = useMemo(() => computeKpis(sesion, movimientos), [sesion, movimientos])

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 4 }}>Caja</div>
          <h1 className="me-page-title" style={{ marginBottom: 4 }}>Turno activo</h1>
          <div style={{ fontSize: 13, color: 'var(--me-text-3)' }}>
            {sesion.nombre_empleado && (
              <><span style={{ fontWeight: 600, color: 'var(--me-text-2)', fontSize: 14 }}>{sesion.nombre_empleado}</span>{' · '}</>
            )}
            Apertura: {fmtHora(sesion.fecha_apertura)}
            {' · '}
            {duracion(sesion.fecha_apertura)} en turno
            {' · '}
            Fondo inicial:{' '}
            <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}>
              ${fmt(sesion.monto_apertura)}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', background: 'var(--me-surface-2)', padding: 3, borderRadius: 10, gap: 2 }}>
            {(['vender', 'movimientos'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 7,
                  border: 'none',
                  background: tab === t ? 'var(--me-surface)' : 'transparent',
                  color: tab === t ? 'var(--me-text)' : 'var(--me-text-3)',
                  fontFamily: 'var(--me-sans)',
                  fontSize: 13,
                  fontWeight: tab === t ? 600 : 400,
                  cursor: 'pointer',
                  boxShadow: tab === t ? 'var(--me-shadow-card)' : 'none',
                  transition: 'all var(--me-dur-short)',
                }}
              >
                {t === 'vender' ? 'Vender' : 'Movimientos'}
              </button>
            ))}
          </div>
          <button className="me-btn me-btn-danger" onClick={() => setShowCierre(true)}>
            Cerrar turno
          </button>
        </div>
      </div>

      {tab === 'vender' ? (
        <NuevaVentaPage embedded embeddedCajaActual={sesion} />
      ) : (
        <>
          {/* KPI strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <KpiCard label="Efectivo en caja" value={kpis.efectivoEnCaja} accent />
            <KpiCard label="Transferencias" value={kpis.transferencias} />
            <KpiCard label="Débito" value={kpis.debito} />
            <KpiCard label="Crédito" value={kpis.credito} />
          </div>

          {/* Two-column main */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24 }}>
            {/* Movimientos */}
            <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--me-line)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span style={{
                  fontFamily: 'var(--me-display)', fontVariationSettings: '"opsz" 24, "wght" 700', fontSize: 15,
                }}>
                  Movimientos del turno
                </span>
                <span style={{ fontSize: 12, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)' }}>
                  {movimientos.length} registros
                </span>
              </div>

              {movimientos.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13 }}>
                  Sin movimientos en este turno — registrá ingresos o egresos en el panel derecho
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="me-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Hora</th>
                        <th>Tipo</th>
                        <th>Método</th>
                        <th>Concepto</th>
                        <th style={{ textAlign: 'right' }}>Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientos.map((m, i) => (
                        <MovimientoRow key={m.id} m={m} i={i} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Nuevo movimiento */}
            <NuevoMovimientoPanel sesionId={sesion.id} usuarioId={usuarioId} />
          </div>
        </>
      )}

      {showCierre && (
        <CierreModal
          sesion={sesion}
          kpis={kpis}
          onClose={() => setShowCierre(false)}
        />
      )}
    </div>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, accent = false }: { label: string; value: number; accent?: boolean }) {
  const isNeg = value < 0
  return (
    <div className="me-card" style={{
      padding: '16px 20px',
      background: accent ? 'var(--me-acid)' : 'var(--me-surface)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
        color: accent ? 'var(--me-acid-ink)' : 'var(--me-text-3)',
        marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{
        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
        fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em',
        color: accent
          ? 'var(--me-acid-ink)'
          : (value === 0 ? 'var(--me-text-3)' : (isNeg ? 'var(--me-bad)' : 'var(--me-text)')),
      }}>
        {isNeg ? '-' : ''}${fmt(Math.abs(value))}
      </div>
    </div>
  )
}

// ─── Movimiento row ──────────────────────────────────────────────────────────

const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo', transferencia: 'Transf.', debito: 'Débito', credito: 'Crédito',
}

function MovimientoRow({ m, i }: { m: CajaMovimiento; i: number }) {
  return (
    <tr style={{ background: i % 2 === 0 ? 'transparent' : 'var(--me-surface-2)' }}>
      <td style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
        {fmtHora(m.created_at)}
      </td>
      <td>
        <span className={`me-pill ${m.tipo === 'ingreso' ? 'me-pill-ok' : 'me-pill-bad'}`}>
          {m.tipo}
        </span>
      </td>
      <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>
        {METODO_LABEL[m.metodo] ?? m.metodo}
      </td>
      <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>
        {m.concepto || <span style={{ color: 'var(--me-text-4)' }}>—</span>}
      </td>
      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
        <span style={{ color: m.tipo === 'ingreso' ? 'var(--me-ok)' : 'var(--me-bad)', fontWeight: 500 }}>
          {m.tipo === 'ingreso' ? '+' : '-'}${fmt(m.monto)}
        </span>
      </td>
    </tr>
  )
}

// ─── Nuevo movimiento panel ──────────────────────────────────────────────────

function NuevoMovimientoPanel({ sesionId, usuarioId }: { sesionId: string; usuarioId: string }) {
  const [tipo, setTipo] = useState<'ingreso' | 'egreso'>('ingreso')
  const [metodo, setMetodo] = useState<MetodoCaja>('efectivo')
  const [monto, setMonto] = useState('')
  const [concepto, setConcepto] = useState('')
  const agregar = useAgregarMovimientoCaja()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const montoNum = parseFloat(monto)
    if (!montoNum || montoNum <= 0) {
      toast.error('El monto debe ser mayor a cero')
      return
    }
    try {
      await agregar.mutateAsync({ sesionId, tipo, metodo, monto: montoNum, concepto, usuarioId })
      setMonto('')
      setConcepto('')
      toast.success(tipo === 'ingreso' ? 'Ingreso registrado' : 'Egreso registrado')
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al registrar')
    }
  }

  return (
    <div className="me-card" style={{ padding: 20, alignSelf: 'flex-start' }}>
      <div style={{
        fontFamily: 'var(--me-display)', fontVariationSettings: '"opsz" 24, "wght" 700',
        fontSize: 15, marginBottom: 16,
      }}>
        Nuevo movimiento
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Tipo toggle */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {(['ingreso', 'egreso'] as const).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              style={{
                padding: '8px 0',
                borderRadius: 8,
                border: `2px solid ${tipo === t
                  ? (t === 'ingreso' ? 'var(--me-ok)' : 'var(--me-bad)')
                  : 'var(--me-line)'}`,
                background: tipo === t
                  ? (t === 'ingreso' ? 'var(--me-ok-soft)' : 'var(--me-bad-soft)')
                  : 'transparent',
                color: tipo === t
                  ? (t === 'ingreso' ? 'var(--me-ok)' : 'var(--me-bad)')
                  : 'var(--me-text-3)',
                fontFamily: 'var(--me-sans)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all var(--me-dur-short)',
              }}
            >
              {t === 'ingreso' ? '+ Ingreso' : '− Egreso'}
            </button>
          ))}
        </div>

        {/* Método */}
        <div>
          <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Método</label>
          <select
            className="me-input"
            style={{ width: '100%' }}
            value={metodo}
            onChange={e => setMetodo(e.target.value as MetodoCaja)}
          >
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="debito">Débito</option>
            <option value="credito">Crédito</option>
          </select>
        </div>

        {/* Monto */}
        <div>
          <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Monto</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--me-text-3)', fontSize: 13, pointerEvents: 'none',
            }}>$</span>
            <input
              className="me-input"
              style={{ width: '100%', paddingLeft: 24, fontFamily: 'var(--me-mono)' }}
              type="number"
              min="0.01"
              step="0.01"
              value={monto}
              onChange={e => setMonto(e.target.value)}
              placeholder="0,00"
            />
          </div>
        </div>

        {/* Concepto */}
        <div>
          <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>
            Concepto{' '}
            <span style={{ color: 'var(--me-text-4)', fontWeight: 400, textTransform: 'none' }}>
              (opcional)
            </span>
          </label>
          <input
            className="me-input"
            style={{ width: '100%' }}
            type="text"
            value={concepto}
            onChange={e => setConcepto(e.target.value)}
            placeholder="Ej: Pago proveedor, retiro..."
            autoComplete="off"
          />
        </div>

        <button
          type="submit"
          className="me-btn me-btn-primary"
          style={{ width: '100%', marginTop: 4 }}
          disabled={agregar.isPending}
        >
          {agregar.isPending ? 'Registrando...' : 'Registrar movimiento'}
        </button>
      </form>
    </div>
  )
}

// ─── Historial de sesiones ────────────────────────────────────────────────────

function HistorialSesiones() {
  const { data: sesiones = [], isLoading } = useCajaSesiones()

  if (!isLoading && sesiones.length === 0) return null

  return (
    <div>
      <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-4)' }}>Historial de turnos</div>
      <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: '24px', color: 'var(--me-text-3)', fontSize: 13 }}>Cargando historial...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="me-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cajero</th>
                  <th>Apertura</th>
                  <th>Cierre</th>
                  <th>Duración</th>
                  <th style={{ textAlign: 'right' }}>Fondo inicial</th>
                  <th style={{ textAlign: 'right' }}>Cierre efectivo</th>
                  <th style={{ textAlign: 'right' }}>Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {sesiones.map((s, i) => {
                  const apertura = new Date(s.fecha_apertura)
                  const cierre = s.fecha_cierre ? new Date(s.fecha_cierre) : null
                  const durMin = cierre
                    ? Math.floor((cierre.getTime() - apertura.getTime()) / 60000)
                    : null
                  const durStr = durMin === null ? '—'
                    : durMin < 60 ? `${durMin} min`
                    : `${Math.floor(durMin / 60)} h ${durMin % 60} min`
                  const diff = s.diferencia ?? 0
                  const diffColor = diff === 0 ? 'var(--me-text-3)' : diff > 0 ? 'var(--me-ok)' : 'var(--me-bad)'

                  return (
                    <tr key={s.id} style={{ background: i % 2 === 1 ? 'var(--me-surface-2)' : 'transparent' }}>
                      <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>
                        {apertura.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--me-text-2)' }}>
                        {s.nombre_empleado
                          ? <span style={{ fontWeight: 500 }}>{s.nombre_empleado}</span>
                          : <span style={{ color: 'var(--me-text-4)' }}>—</span>
                        }
                      </td>
                      <td style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
                        {apertura.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)' }}>
                        {cierre ? cierre.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--me-text-3)' }}>{durStr}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                        ${fmt(s.monto_apertura)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13 }}>
                        {s.monto_contado !== null ? `$${fmt(s.monto_contado)}` : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 13, fontWeight: 600, color: diffColor }}>
                        {diff === 0 ? '—' : `${diff > 0 ? '+' : '-'}$${fmt(Math.abs(diff))}`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Cierre modal ─────────────────────────────────────────────────────────────

type Kpis = { efectivoEnCaja: number; transferencias: number; debito: number; credito: number }

function CierreModal({
  sesion, kpis, onClose,
}: { sesion: CajaSesion; kpis: Kpis; onClose: () => void }) {
  const [montoContado, setMontoContado] = useState('')
  const cerrar = useCerrarCaja()

  const montoNum = parseFloat(montoContado) || 0
  const diferencia = montoNum - kpis.efectivoEnCaja
  const hasMonto = montoContado !== ''

  const diferenciaColor = diferencia >= 0 ? 'var(--me-ok)' : 'var(--me-bad)'
  const diferenciaLabel = diferencia === 0 ? 'Sin diferencia' : diferencia > 0 ? 'Sobrante' : 'Faltante'
  const diferenciaBg = diferencia >= 0 ? 'var(--me-ok-soft)' : 'var(--me-bad-soft)'

  const digitales = [
    { label: 'Transferencias', value: kpis.transferencias },
    { label: 'Débito',         value: kpis.debito },
    { label: 'Crédito',        value: kpis.credito },
  ].filter(d => d.value !== 0)

  async function handleCerrar() {
    try {
      await cerrar.mutateAsync({
        sesionId: sesion.id,
        montoContado: montoNum,
        montoCierre: kpis.efectivoEnCaja,
        diferencia,
      })
      toast.success('Turno cerrado correctamente')
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al cerrar caja')
    }
  }

  return (
    <>
      <div className="me-overlay me-overlay-in" onClick={onClose} />
      <div className="me-modal me-modal-in" style={{ maxWidth: 440 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid var(--me-line)',
        }}>
          <h2 className="me-section-title">Cierre de turno</h2>
          <button className="me-icon-btn" onClick={onClose}><CloseIcon size={18} /></button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Efectivo */}
          <div>
            <div className="me-eyebrow" style={{ marginBottom: 8 }}>Efectivo</div>
            <div style={{
              padding: '10px 14px', borderRadius: 10, background: 'var(--me-surface-2)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 10,
            }}>
              <span style={{ fontSize: 13, color: 'var(--me-text-2)' }}>Esperado en caja</span>
              <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 600, fontSize: 15 }}>
                ${fmt(kpis.efectivoEnCaja)}
              </span>
            </div>

            <label className="me-label" style={{ display: 'block', marginBottom: 8 }}>
              Contado físicamente
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--me-text-3)', fontSize: 14, pointerEvents: 'none',
              }}>$</span>
              <input
                className="me-input"
                style={{ width: '100%', paddingLeft: 24, fontFamily: 'var(--me-mono)', fontSize: 16 }}
                type="number" min="0" step="0.01"
                value={montoContado}
                onChange={e => setMontoContado(e.target.value)}
                placeholder="0,00"
                autoFocus
              />
            </div>

            {hasMonto && (
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginTop: 8, padding: '8px 14px', borderRadius: 8, background: diferenciaBg,
              }}>
                <span style={{ fontSize: 13, color: diferenciaColor }}>{diferenciaLabel}</span>
                <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 600, fontSize: 15, color: diferenciaColor }}>
                  {diferencia > 0 ? '+' : diferencia < 0 ? '−' : ''}${fmt(Math.abs(diferencia))}
                </span>
              </div>
            )}
          </div>

          {/* Métodos digitales */}
          {digitales.length > 0 && (
            <div>
              <div className="me-eyebrow" style={{ marginBottom: 8 }}>Métodos digitales</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {digitales.map(d => (
                  <div key={d.label} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '8px 14px', borderRadius: 8, background: 'var(--me-surface-2)',
                  }}>
                    <span style={{ fontSize: 13, color: 'var(--me-text-2)' }}>{d.label}</span>
                    <span style={{
                      fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                      fontSize: 14, fontWeight: 600,
                      color: d.value < 0 ? 'var(--me-bad)' : 'var(--me-text)',
                    }}>
                      {d.value < 0 ? '−' : ''}${fmt(Math.abs(d.value))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '16px 24px', borderTop: '1px solid var(--me-line)',
        }}>
          <button className="me-btn me-btn-ghost" onClick={onClose}>Cancelar</button>
          <button
            className="me-btn me-btn-primary"
            onClick={handleCerrar}
            disabled={cerrar.isPending || !hasMonto}
          >
            {cerrar.isPending ? 'Cerrando...' : 'Cerrar turno'}
          </button>
        </div>
      </div>
    </>
  )
}
